import { runWhenIdle } from './runWhenIdle';

// Loaders for chunks a page's first screen leaves out but a later step needs
// (the interview's live call and later form steps, the job page's tabs, the
// transcript drawer). Each fetches its chunk once, forgets a failed fetch so
// the next call retries it, and can warm the chunk once the page is idle so
// the step that needs it does not wait.

export interface ChunkLoader<T> {
  /** Fetches the chunk; concurrent and later calls share one fetch. */
  load: () => Promise<T>;
  /** The chunk's module once it has loaded, else null. */
  loaded: () => T | null;
  /**
   * Starts fetching the chunk once the page is idle. Returns a function that
   * cancels a prefetch that has not started yet. A failed prefetch is left for
   * `load` to retry and report.
   */
  prefetchWhenIdle: () => () => void;
}

// Same deadlines as the variable-catalog warm-up: WebKit has no
// requestIdleCallback, so Safari and iOS take the timer.
const PREFETCH_IDLE_TIMEOUT_MS = 5000;
const PREFETCH_TIMER_DELAY_MS = 1000;

export function createChunkLoader<T>(
  importChunk: () => Promise<T>
): ChunkLoader<T> {
  let pendingModule: Promise<T> | null = null;
  let loadedModule: T | null = null;

  const load = (): Promise<T> => {
    if (!pendingModule) {
      const pending = importChunk();
      pendingModule = pending;
      // Registered before any caller's handler, so `loaded()` is set by the
      // time a caller's `await load()` resumes.
      pending.then(
        (module) => {
          loadedModule = module;
        },
        () => {
          if (pendingModule === pending) {
            pendingModule = null;
          }
        }
      );
    }
    return pendingModule;
  };

  const prefetchWhenIdle = (): (() => void) => {
    const prefetch = () => {
      load().catch(() => {
        // swallow-ok: warm-up only; the step that needs the chunk loads it again and handles a failure there
      });
    };
    return runWhenIdle(prefetch, {
      idleTimeoutMs: PREFETCH_IDLE_TIMEOUT_MS,
      fallbackDelayMs: PREFETCH_TIMER_DELAY_MS,
    });
  };

  return { load, loaded: () => loadedModule, prefetchWhenIdle };
}
